import { useEditorSelector } from "lib/studio/editorContext";
import type { ReactNode } from "react";
import cx from "ui/cx";
import type { TooltipContentProps } from "ui/popovers/Tooltip";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./StudioTooltip.module.scss";

interface StudioTooltipProps {
  children: ReactNode;
  content: ReactNode;
  delayDuration?: number;
  side?: TooltipContentProps["side"];
  sideOffset?: number;
  align?: TooltipContentProps["align"];
  className?: string;
  disabled?: boolean;
  maxContentWidth?: number;
  disableWhenCmdActive?: boolean;
}

type StudioTooltipBaseProps = Omit<StudioTooltipProps, "disableWhenCmdActive">;

function StudioTooltipBase({
  children,
  content,
  delayDuration = 800,
  side = "top",
  sideOffset,
  align,
  className,
  disabled = false,
  maxContentWidth,
}: StudioTooltipBaseProps) {
  const isDisabled = disabled || !content;

  if (isDisabled) {
    return <>{children}</>;
  }

  const tooltipContent = maxContentWidth ? <div style={{ maxWidth: `${maxContentWidth}px` }}>{content}</div> : content;

  return (
    <Tooltip.Root delayDuration={delayDuration}>
      <Tooltip.Trigger>{children}</Tooltip.Trigger>
      <Tooltip.Content side={side} sideOffset={sideOffset} align={align} className={cx(styles.content, className)}>
        {tooltipContent}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

function StudioTooltipWithCmdActive(props: StudioTooltipBaseProps) {
  const { cmdActive } = useEditorSelector((ed) => ({
    cmdActive: !!ed.cmdActive,
  }));

  return <StudioTooltipBase {...props} disabled={props.disabled || cmdActive} />;
}

/**
 * Reusable tooltip component for Studio that wraps Radix Tooltip primitives
 * with consistent styling and defaults.
 */
export default function StudioTooltip({
  children,
  content,
  delayDuration = 800,
  side = "top",
  sideOffset,
  align,
  className,
  disabled = false,
  maxContentWidth,
  disableWhenCmdActive = false,
}: StudioTooltipProps) {
  const baseProps: StudioTooltipBaseProps = {
    children,
    content,
    delayDuration,
    side,
    sideOffset,
    align,
    className,
    disabled,
    maxContentWidth,
  };

  if (disableWhenCmdActive) {
    return <StudioTooltipWithCmdActive {...baseProps} />;
  }

  return <StudioTooltipBase {...baseProps} />;
}
